import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
    Button,
    Typography,
    Row,
    Col,
    Tag,
    Input,
    DatePicker,
    Select,
    Layout,
    Form,
    Switch,
    Flex,
    message,
    Upload,
    Radio,
} from "antd";
import { UploadOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { CalendarIcon } from "../../assets/CustomIcon";
import MapImg from "../../assets/images/map.svg";

import { getBase64, getImageUrl } from "../../utils/appUtils";
import { EVENT_TYPES, VALIDATION_MESSAGES, PROMOTION_TAGS } from "../../utils/constants";
import { buildEventPayload } from "../../utils/payloadBuilderUtil";
import Loader from "../../components/common/Loader";
import GifLoader from "../../components/common/GifLoader";
import PageLayout from "../../components/common/Layouts/PageLayout";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import RichTextEditor from "../../components/common/RichTextEditor";
import {
    createEvent,
    selectCreateEventLoading,
    selectCreateEventError,
    selectCreateEventSuccess,
    selectCreatedEvent,
    resetCreateEventSuccess,
    clearCreateEventError,
    // Publish Event API
    publishEvent,
    selectPublishEventLoading,
    selectPublishEventError,
    selectPublishEventSuccess,
    resetPublishEventSuccess,
    clearPublishEventError,
    // Category API
    fetchEventCategories,
    selectEventCategories,
    selectFetchCategoriesLoading,
    // SubCategory API
    fetchEventSubCategories,
    selectEventSubCategories,
    selectFetchSubCategoriesLoading,
    clearSubCategories,
    // Currencies API
    fetchCurrencies,
    selectCurrencies,
    selectFetchCurrenciesLoading,
    // Ticket Categories API
    fetchTicketCategories,
    selectTicketCategories,
    selectFetchTicketCategoriesLoading,
    // Update Event API
    updateEvent,
    selectUpdateEventLoading,
    selectUpdateEventError,
    selectUpdateEventSuccess,
    resetUpdateEventSuccess,
    selectMyEvents,
    fetchMyEvents,
    // Fetch Event By ID (for edit mode)
    fetchEventById,
    selectCurrentEvent,
    selectFetchEventByIdLoading,
    selectFetchEventByIdError,
    selectFetchEventByIdStatus,
    clearUpdateEventError,
} from "../../redux/eventSlice";
import {
    fetchVenues,
    selectVenues,
    selectFetchVenuesLoading,
} from "../../redux/venueSlice";

const { Text } = Typography;
const { Content } = Layout;

// Available event tags
const AVAILABLE_EVENT_TAGS = [
    'Business', 'Pharma', 'Mumbai', 'Delhi',
    'Government', 'Tech', 'Startup', 'Corporate',
    'Conference', 'Workshop', 'Networking', 'Social'
];

export default function EventAddEditPage({ isEmbedded = false }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // State variables
    const [actionType, setActionType] = useState(null);
    const [selectedTags, setSelectedTags] = useState([]);
    const [hasPrefilled, setHasPrefilled] = useState(false);
    const [gifLoader, setGifLoader] = useState(false);
    const [publishStatus, setPublishStatus] = useState(false);

    // Create Event state
    const createEventLoading = useSelector(selectCreateEventLoading);
    const createEventError = useSelector(selectCreateEventError);
    const createEventSuccess = useSelector(selectCreateEventSuccess);
    const createdEvent = useSelector(selectCreatedEvent);

    // Publish Event state
    const publishEventLoading = useSelector(selectPublishEventLoading);
    const publishEventError = useSelector(selectPublishEventError);
    const publishEventSuccess = useSelector(selectPublishEventSuccess);

    // Edit Event state
    const updateEventLoading = useSelector(selectUpdateEventLoading);
    const updateEventError = useSelector(selectUpdateEventError);
    const updateEventSuccess = useSelector(selectUpdateEventSuccess);

    /** Fetch pre-required data from redux **/
    // Events
    const allEvents = useSelector(selectMyEvents);
    const currentEvent = useSelector(selectCurrentEvent);
    const fetchEventByIdLoading = useSelector(selectFetchEventByIdLoading);
    const fetchEventByIdError = useSelector(selectFetchEventByIdError);
    const fetchStatus = useSelector(selectFetchEventByIdStatus);
    // Venues
    const venues = useSelector(selectVenues);
    const venuesLoading = useSelector(selectFetchVenuesLoading);
    // Categories
    const eventCategories = useSelector(selectEventCategories);
    const categoriesLoading = useSelector(selectFetchCategoriesLoading);
    // SubCategories
    const eventSubCategories = useSelector(selectEventSubCategories);
    const subCategoriesLoading = useSelector(selectFetchSubCategoriesLoading);
    // Currencies
    const currencies = useSelector(selectCurrencies);
    const currenciesLoading = useSelector(selectFetchCurrenciesLoading);
    // Ticket Categories
    const ticketCategories = useSelector(selectTicketCategories);
    const ticketCategoriesLoading = useSelector(selectFetchTicketCategoriesLoading);

    const [form] = Form.useForm();
    const { id } = useParams(); // Get event ID for edit mode
    const isEditMode = !!id;
    const dynamicEventType = Form.useWatch('eventType', form);
    const ticketMode = Form.useWatch('ticketMode', form) || 'per_seat';

    // Fetch initial data on component mount
    useEffect(() => {
        dispatch(fetchVenues());
        dispatch(fetchEventCategories());
        dispatch(fetchCurrencies());
        dispatch(fetchTicketCategories());
        if (isEditMode && id) {
            dispatch(fetchEventById(id));
            // Also fetch all events for fallback data (since GET /event/{id} returns null from backend)
            dispatch(fetchMyEvents());
        }
    }, [dispatch, isEditMode, id]);

    // Pre-fill form in Edit Mode
    useEffect(() => {
        // Only run in edit mode with required data loaded
        if (!isEditMode || !id) return;

        // Wait for allEvents data to load (primary source since individual fetch returns null)
        if (!allEvents?.data || allEvents.data.length === 0) {
            console.log('⏳ Edit mode: Waiting for events list to load...');
            return;
        }

        // Find the event to edit from the list
        let eventToEdit = allEvents.data.find(e => String(e.id) === String(id));

        // Try individual fetch as backup (in case backend fixes it later)
        if (!eventToEdit && currentEvent?.data?.id) {
            eventToEdit = currentEvent.data;
        } else if (!eventToEdit && currentEvent?.id) {
            eventToEdit = currentEvent;
        }

        if (!eventToEdit) {
            console.log('❌ Event not found with ID:', id);
            return;
        }

        console.log('🎯 Editing Event:', eventToEdit);


        if (!Array.isArray(ticketCategories) || ticketCategories.length === 0) {
            console.log('⏳ Waiting for ticketCategories to load...');
            return;
        }
        if (!Array.isArray(currencies) || currencies.length === 0) {
            console.log('⏳ Waiting for currencies to load...');
            return;
        }
        if (hasPrefilled) {
            console.log('✅ Form already prefilled, skipping...');
            return;
        }

        console.log("🎯 Editing Event (from individual fetch):", eventToEdit);
        console.log("📸 Event Media:", eventToEdit.event_media);
        console.log("📋 Available Ticket Categories:", ticketCategories);
        console.log("💰 Available Currencies:", currencies);

        // Format dates
        const startDate = eventToEdit.event_start_date_time ? dayjs(eventToEdit.event_start_date_time) : null;
        const endDate = eventToEdit.event_end_date_time ? dayjs(eventToEdit.event_end_date_time) : null;
        const promoStartDate = eventToEdit.event_promo_start_date ? dayjs(eventToEdit.event_promo_start_date) : null;
        const promoEndDate = eventToEdit.event_promo_end_date ? dayjs(eventToEdit.event_promo_end_date) : null;

        // Format images for Upload component
        // Handle both event_media (array) and event_banner (string URL from list API)
        let fileList = [];

        const existingMedia =
            Array.isArray(eventToEdit.event_media) && eventToEdit.event_media.length > 0
                ? eventToEdit.event_media
                : Array.isArray(eventToEdit.media)
                    ? eventToEdit.media
                    : [];

        if (existingMedia.length > 0) {
            fileList = existingMedia
                .filter(media => !media.is_delete)
                .map((media, index) => {
                    const imageUrl = getImageUrl(media);

                    return {
                        uid: `existing-${media.id || index}`,
                        name:
                            media.file_name ||
                            `image-${media.id || index}.${media.file_extension || "jpg"}`,
                        status: "done",
                        url: imageUrl,
                        thumbUrl: imageUrl,
                        originFileObj: null,
                        isExisting: true,
                    };
                })
                .filter(item => item.url);
        } else if (eventToEdit.event_banner) {
            // Fallback: event_banner string URL (from list API)
            fileList = [{
                uid: 'existing-banner-0',
                name: 'event-banner.jpg',
                status: 'done',
                url: getImageUrl({ file_path: eventToEdit.event_banner }),
                thumbUrl: getImageUrl({ file_path: eventToEdit.event_banner }),
                originFileObj: null,
                isExisting: true
            }];
        }
        console.log("🖼️ Pre-fill Images:", fileList);

        // Format tickets - Map API field names to form field names
        const tickets = eventToEdit.tickets && eventToEdit.tickets.length > 0 ? eventToEdit.tickets.map(t => {
            return {
                id: t.id, // Preserve Ticket ID for updates
                seatingCategory: t.seating_category_id || t.ticket_category_id,
                currency: t.currency_id,
                price: t.price,
                numberOfSeats: t.number_of_seats || t.seat_count
            };
        }) : [{}]; // Default empty ticket if none
        console.log("🎫 Pre-fill Tickets:", tickets);

        const eventTables = eventToEdit.tables && eventToEdit.tables.length > 0 ? eventToEdit.tables.map(t => ({
            id: t.id,
            label: t.label,
            personsCount: t.persons_count,
            currency: t.currency_id || t.currency?.id,
            price: t.price,
            quantity: t.quantity,
        })) : [{}];
        console.log("🪑 Pre-fill Tables:", eventTables);

        // Set ALL form values together
        const formValues = {
            "event-name": eventToEdit.event_name,
            "eventDescription": eventToEdit.event_description,
            "startDate": startDate,
            "endDate": endDate,
            "promoStartDate": promoStartDate,
            "promoEndDate": promoEndDate,
            "category": eventToEdit.event_category?.id || eventToEdit.category_id,
            "subcategory": undefined,
            "venue": eventToEdit.venue_id,
            "promoTag": eventToEdit.promotion_tag,
            "loading_contacts": eventToEdit.contact_list ? eventToEdit.contact_list.split(',') : [],
            "event_allow_registration": eventToEdit.event_allow_registration,
            "event_allow_promotion": eventToEdit.event_allow_promotion,
            "isRsvp": eventToEdit.isRsvp,
            "isRefundable": eventToEdit.is_refundable,
            "isTaxable": eventToEdit.is_taxable,
            "addConvenienceFee": eventToEdit.add_convenience_fee,
            "ticketCategories": tickets,
            "ticketMode": eventToEdit.ticket_mode || "per_seat",
            "eventTables": eventTables,
            "eventImages": fileList,
            "eventType": eventToEdit.event_type_id != null
            ? String(eventToEdit.event_type_id)
            : undefined
        };

        console.log("📝 Setting form values:", formValues);
        form.setFieldsValue(formValues);

        // Mark as prefilled to avoid re-running
        setHasPrefilled(true);
        // set publish status
        setPublishStatus(eventToEdit.is_event_published);

        // Handle dependent data like subcategories
        const categoryId =
            eventToEdit.event_category?.id ?? eventToEdit.category_id;

        dispatch(clearSubCategories());

        if (categoryId) {
            dispatch(fetchEventSubCategories(categoryId));
        }

        // Handle tags
        if (eventToEdit.event_tags) {
            setSelectedTags(eventToEdit.event_tags.split(','));
        }
    }, [isEditMode, id, currentEvent, form, dispatch, ticketCategories, currencies, hasPrefilled, fetchEventByIdLoading, fetchEventByIdError, fetchStatus, allEvents]);


    // Set existing subcategory only after valid options have loaded.
    // If an old event contains a subcategory that does not belong to
    // its category, leave the field empty and require a valid selection.
    useEffect(() => {
        if (!isEditMode || !id || !hasPrefilled || subCategoriesLoading) {
            return;
        }

        if (!Array.isArray(eventSubCategories)) {
            return;
        }

        let eventToEdit = allEvents?.data?.find(
            event => String(event.id) === String(id)
        );

        if (!eventToEdit && currentEvent?.data?.id) {
            eventToEdit = currentEvent.data;
        } else if (!eventToEdit && currentEvent?.id) {
            eventToEdit = currentEvent;
        }

        if (!eventToEdit) {
            return;
        }

        const storedSubcategoryId =
            eventToEdit.event_sub_category?.id ??
            eventToEdit.sub_category_id;

        if (storedSubcategoryId == null) {
            form.setFieldsValue({ subcategory: undefined });
            return;
        }

        const isValidSubcategory = eventSubCategories.some(
            subCategory =>
                String(subCategory.id) === String(storedSubcategoryId)
        );

        form.setFieldsValue({
            subcategory: isValidSubcategory
                ? storedSubcategoryId
                : undefined
        });
    }, [
        isEditMode,
        id,
        hasPrefilled,
        subCategoriesLoading,
        eventSubCategories,
        allEvents,
        currentEvent,
        form
    ]);


    /*
     * Handlers
     */
    // Handle category change - fetch subcategories
    const handleCategoryChange = (categoryId) => {
        // Clear subcategory selection and data
        form.setFieldsValue({ subcategory: undefined });
        dispatch(clearSubCategories());

        // Fetch subcategories for selected category
        if (categoryId) {
            dispatch(fetchEventSubCategories(categoryId));
        }
    };

    // Toggle tag selection
    const handleTagClick = (tag) => {
        setSelectedTags(prev => {
            if (prev.includes(tag)) {
                return prev.filter(t => t !== tag);
            } else {
                return [...prev, tag];
            }
        });
    };

    // Handle successful event creation.
    // Drafts stop after creation; normal creates must call the dedicated publish API.
    useEffect(() => {
        if (!createEventSuccess) return;

        if (actionType === 'draft') {
            setTimeout(() => {
                setGifLoader(false);
                dispatch(resetCreateEventSuccess());
                navigate("/events");
            }, 5000);
            return;
        }

        if (actionType === 'publish') {
            const eventId =
                createdEvent?.event_id ||
                createdEvent?.data?.event_id ||
                createdEvent?.id ||
                createdEvent?.data?.id;

            if (!eventId) {
                setGifLoader(false);
                message.error(
                    'Event was created but no event ID was returned, so it could not be published.'
                );
                dispatch(resetCreateEventSuccess());
                return;
            }

            console.log('Publishing newly created event:', eventId);

            // Prevent this effect from firing again while the publish request is in progress.
            dispatch(resetCreateEventSuccess());
            dispatch(publishEvent(eventId));
        }
    }, [createEventSuccess, createdEvent, actionType, dispatch, navigate]);

    // Redirect only after the dedicated publish endpoint succeeds.
    useEffect(() => {
        if (publishEventSuccess) {
            setTimeout(() => {
                setGifLoader(false);
                dispatch(resetPublishEventSuccess());
                navigate("/events");
            }, 5000);
        }
    }, [publishEventSuccess, dispatch, navigate]);

    // Handle publish error
    useEffect(() => {
        if (publishEventError) {
            setGifLoader(false);
            message.error(
                publishEventError || 'Event was created but could not be published.'
            );
            dispatch(clearPublishEventError());
        }
    }, [publishEventError, dispatch]);

    // Handle redirect on successful event update
    useEffect(() => {
        if (updateEventSuccess && actionType === 'publish') {
            setTimeout(() => {
                setGifLoader(false);
                dispatch(resetUpdateEventSuccess());
                navigate("/events");
            }, 5000);
        }
    }, [updateEventSuccess, actionType, dispatch, navigate]);

    // Handle create error
    useEffect(() => {
        if (createEventError) {
            setGifLoader(false);
            message.error(createEventError || 'Failed to create event');
            dispatch(clearCreateEventError());
        }
    }, [createEventError, dispatch]);

    // Handle update error
    useEffect(() => {
        if (updateEventError) {
            setGifLoader(false);
            message.error(updateEventError || 'Failed to update event');
            dispatch(clearUpdateEventError());
        }
    }, [updateEventError, dispatch]);

    /**
     * Handles form submission for draft save.
     */
    const handleSaveDraft = async () => {
        setActionType('draft');
        setGifLoader(true);
        const values = form.getFieldsValue();
        // Skip validation for draft

        const processedGallery = await processImages(values.eventImages);
        // Add selected tags to values
        const valuesWithTags = { ...values, eventTags: getTagsString() };
        // Build the final payload using the utility function
        const formattedValues = buildEventPayload(valuesWithTags, processedGallery, true, false);

        // CREATE MODE: Send create draft request
        dispatch(createEvent(formattedValues));
    };

    /**
     * Handles form submission for both Create and Update scenarios.
     */
    const onFinish = async (values) => {
        setActionType('publish');
        setGifLoader(true);

        // Process images to base64 format for API
        const processedGallery = await processImages(values.eventImages);
        // Add selected tags to form values
        const valuesWithTags = { ...values, eventTags: getTagsString() };
        // Build the final payload using the utility function
        // New events should be published immediately when submitted.
        // In edit mode, preserve the event's existing publication status.
        const isPublished = isEditMode ? publishStatus : true;
        const formattedValues = buildEventPayload(valuesWithTags, processedGallery, false, isPublished);

        // Dispatch appropriate action based on mode
        if (isEditMode) {
            // UPDATE MODE: Send update request with event ID
            console.log("Updating event with ID:", id);
            dispatch(updateEvent(formattedValues, id));
        } else {
            // CREATE MODE: Send create request
            dispatch(createEvent(formattedValues));
        }
    };

    /*** Helper Functions ***/
    // Get tags as comma-separated string for API
    const getTagsString = () => selectedTags.join(',');


    // Helper to process images - handles both new uploads and existing URLs
    const processImages = async (imageFiles) => {
        let processedGallery = [];
        if (imageFiles && imageFiles.length > 0) {
            processedGallery = await Promise.all(
                imageFiles.map(async (fileItem) => {
                    // Check if this is an existing image (has url but no originFileObj)
                    // Existing unchanged image.
                    // Do not resend it during an event update; the backend
                    // will keep the existing Media record unchanged.
                    if (fileItem.isExisting && !fileItem.originFileObj) {
                        return null;
                    }

                    // New upload - convert to base64
                    const file = fileItem.originFileObj || fileItem;
                    if (!file || typeof file.name === 'undefined') return null;

                    try {
                        const base64 = await getBase64(file);
                        const fileName = file.name ? file.name.substring(0, file.name.lastIndexOf('.')) : 'image';
                        const fileExtension = file.name ? file.name.split('.').pop() : 'jpg';

                        return {
                            file: base64, // Send full base64 string
                            file_name: fileName,
                            file_extension: fileExtension,
                            file_location: "banner",
                            file_type: "EVENT"
                        };
                    } catch (error) {
                        console.error("Error processing image:", error);
                        return null;
                    }
                })
            );
        }
        return processedGallery.filter(item => item !== null);
    };

    // Normalize file input
    const normFile = (e) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    // const users = [
    //     { id: 1, name: "Stave Smith", status: "Friend" },
    //     { id: 2, name: "Stave Smith", status: "Friend" },
    //     { id: 3, name: "Stave Smith", status: "Friend" },
    //     { id: 4, name: "Stave Smith", status: "Friend" },
    //     { id: 5, name: "Stave Smith", status: "Friend" },
    //     { id: 6, name: "Stave Smith", status: "Friend" },
    const Wrapper = ({ children }) => {
        if (isEmbedded) {
            return <>{children}</>;
        }
        return (
            <Content className={gifLoader ? "is-loading-blur" : ""}>
                <div className="pageCard">
                    {children}
                </div>
            </Content>
        );
    };

    const content = (
        <>
            {gifLoader && (
                <div className="loader-fixed">
                    <GifLoader />
                </div>
            )}
            {/* SEO HELMET WRAPPER */}
            {!isEmbedded && (
                <HelmetWrapper
                    title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai"
                    description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
                    and city-specific visibility for organizers and users."
                    keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
                    online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
                    Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
                    Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
                />
            )}
            <Wrapper>
                <Loader spinning={createEventLoading || publishEventLoading || updateEventLoading} tip={isEditMode ? "Updating event..." : "Creating event..."}>
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={16}>
                            <Form
                                layout="vertical"
                                form={form}
                                onFinish={onFinish}
                                initialValues={{
                                    ticketCategories: [{}],
                                    eventTables: [{}],
                                    ticketMode: "per_seat",
                                    eventType: "1"
                                }}
                            >
                                <Form.Item
                                    label="Event Name"
                                    name="event-name"
                                    rules={[
                                        { required: true, message: VALIDATION_MESSAGES.EVENT_NAME_REQUIRED },
                                    ]}
                                >
                                    <Input placeholder="Event Name" size="large" />
                                </Form.Item>

                                <Form.Item
                                    label="Event Description"
                                    name="eventDescription"
                                    valuePropName="value"
                                    trigger="onChange"
                                    getValueFromEvent={(html) => html}
                                    rules={[
                                        { required: true, message: VALIDATION_MESSAGES.EVENT_DESC_REQUIRED },
                                    ]}
                                >
                                    <RichTextEditor placeholder="Event Description" />
                                </Form.Item>

                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Event Images"
                                            name="eventImages"
                                            rules={[
                                                { required: !isEditMode, message: VALIDATION_MESSAGES.EVENT_IMAGE_REQUIRED },
                                            ]}
                                            valuePropName="fileList"
                                            getValueFromEvent={normFile}
                                        >
                                            <Upload
                                                name="logo"
                                                listType="picture"
                                                accept="image/*"
                                                beforeUpload={(file) => {
                                                    const isImage = file.type.startsWith('image/');
                                                    if (!isImage) {
                                                        message.error(`${file.name} is not a valid image file!`);
                                                        return Upload.LIST_IGNORE;
                                                    }
                                                    return false;
                                                }}
                                            >
                                                <Button size="large" icon={<UploadOutlined />}>
                                                    Select file from device
                                                </Button>
                                            </Upload>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Event Start Date & Time"
                                            name="startDate"
                                            rules={[
                                                { required: true, message: VALIDATION_MESSAGES.START_DATE_REQUIRED },
                                            ]}
                                        >
                                            <DatePicker
                                                showTime
                                                size="large"
                                                style={{ width: "100%" }}
                                                placeholder="Event Start Date & Time"
                                                suffixIcon={<CalendarIcon />}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Event End Date & Time"
                                            name="endDate"
                                            rules={[
                                                { required: true, message: VALIDATION_MESSAGES.END_DATE_REQUIRED },
                                            ]}
                                        >
                                            <DatePicker
                                                showTime
                                                size="large"
                                                style={{ width: "100%" }}
                                                placeholder="Event End Date & Time"
                                                suffixIcon={<CalendarIcon />}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Listing Promo Date & Time" name="promoStartDate">
                                            <DatePicker
                                                showTime
                                                size="large"
                                                style={{ width: "100%" }}
                                                placeholder="Listing Promo Date & Time"
                                                suffixIcon={<CalendarIcon />}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Listing Expiry Date & Time" name="promoEndDate">
                                            <DatePicker
                                                showTime
                                                size="large"
                                                style={{ width: "100%" }}
                                                placeholder="Listing Expiry Date & Time"
                                                suffixIcon={<CalendarIcon />}
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Event Type" name="eventType">
                                            <Select
                                                size="large"
                                                placeholder="Event Type"
                                            >
                                                {Array.isArray(EVENT_TYPES) && EVENT_TYPES.map((eventObj) => (
                                                    <Select.Option key={eventObj.value} value={eventObj.value}>
                                                        {eventObj.label}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Event Category"
                                            name="category"
                                            rules={[
                                                { required: true, message: VALIDATION_MESSAGES.EVENT_CATEGORY_REQUIRED },
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                placeholder="Event Category"
                                                loading={categoriesLoading}
                                                onChange={handleCategoryChange}
                                            >
                                                {Array.isArray(eventCategories) && eventCategories.map((category) => (
                                                    <Select.Option key={category.id} value={category.id}>
                                                        {category.category_name || category.name || category.title}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Event Sub category"
                                            name="subcategory"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: "Please select an event sub category"
                                                }
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                placeholder="Event Sub category"
                                                loading={subCategoriesLoading}
                                                disabled={eventSubCategories.length === 0}
                                            >
                                                {Array.isArray(eventSubCategories) && eventSubCategories.map((subCategory) => (
                                                    <Select.Option key={subCategory.id} value={subCategory.id}>
                                                        {subCategory.sub_category_name || subCategory.name || subCategory.title}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Select Venue"
                                            name="venue"
                                            rules={[
                                                { required: true, message: VALIDATION_MESSAGES.VENUE_REQUIRED },
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                placeholder="Select Venue"
                                                loading={venuesLoading}
                                                showSearch
                                                optionFilterProp="children"
                                            >
                                                {Array.isArray(venues) && venues.map((venue) => (
                                                    <Select.Option key={venue.id} value={venue.id}>
                                                        {venue.name}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} md={12}>
                                        <Form.Item label="Promotion Tag" name="promoTag">
                                            <Select
                                                size="large"
                                                placeholder="Promotion Tag"
                                                options={PROMOTION_TAGS}
                                            />
                                        </Form.Item>
                                    </Col>

                                    {/* Dynamic Ticket/Seating Categories or Table Options */}
                                    {dynamicEventType !== '1' && (
                                        <Col xs={24}>
                                            <Form.Item
                                                label="Ticket Mode"
                                                name="ticketMode"
                                                rules={[{ required: true, message: "Please select ticket mode" }]}
                                            >
                                                <Radio.Group>
                                                    <Radio value="per_seat">Per Seat / Ticket</Radio>
                                                    <Radio value="per_table">Per Table</Radio>
                                                </Radio.Group>
                                            </Form.Item>

                                            {ticketMode === 'per_seat' && (
                                            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                                                <Form.List name="ticketCategories">
                                                    {(fields, { add, remove }) => (
                                                        <>
                                                            {fields.map(({ key, name, ...restField }, index) => (
                                                                <div key={key} style={{ position: 'relative', marginBottom: 24, padding: '10px 0', borderBottom: index !== fields.length - 1 ? '1px dashed #d9d9d9' : 'none' }}>
                                                                    {/* Hidden field to preserve ticket ID for updates */}
                                                                    <Form.Item
                                                                        {...restField}
                                                                        name={[name, 'id']}
                                                                        hidden
                                                                    >
                                                                        <Input type="hidden" />
                                                                    </Form.Item>
                                                                    {fields.length > 1 && (
                                                                        <Button
                                                                            type="text"
                                                                            danger
                                                                            icon={<MinusCircleOutlined />}
                                                                            onClick={() => remove(name)}
                                                                            style={{ position: 'absolute', right: 0, top: -5, zIndex: 1 }}
                                                                        />
                                                                    )}
                                                                    <Row gutter={[24, 24]}>
                                                                        <Col xs={24} md={12}>
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'seatingCategory']}
                                                                                label="Seating / Tickets Category"
                                                                                rules={[{ required: true, message: VALIDATION_MESSAGES.SEATING_CATEGORY_REQUIRED }]}
                                                                            >
                                                                                <Select
                                                                                    placeholder="Category"
                                                                                    size="large"
                                                                                    loading={ticketCategoriesLoading}
                                                                                >
                                                                                    {Array.isArray(ticketCategories) && ticketCategories.map((cat) => (
                                                                                        <Select.Option key={cat.id} value={cat.id}>
                                                                                            {cat.category_name || cat.name}
                                                                                        </Select.Option>
                                                                                    ))}
                                                                                </Select>
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={12}>

                                                                            {/* to check total limitations of ticckets */}
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'numberOfSeats']}
                                                                                label="No. of Seats / Tickets"
                                                                                rules={[
                                                                                    { required: true, message: VALIDATION_MESSAGES.SEATS_NO_REQUIRED },
                                                                                ]}
                                                                            >
                                                                                <Input type="number" placeholder="Count" size="large" />
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={12}>
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'currency']}
                                                                                label="Currency"
                                                                                rules={[{ required: true, message: VALIDATION_MESSAGES.CURRENCY_REQUIRED }]}
                                                                            >
                                                                                <Select
                                                                                    placeholder="Currency"
                                                                                    size="large"
                                                                                    loading={currenciesLoading}
                                                                                >
                                                                                    {Array.isArray(currencies) && currencies.map((curr) => (
                                                                                        <Select.Option key={curr.id} value={curr.id}>
                                                                                            {curr.currency_code || curr.name || curr.code || String(curr.id)}
                                                                                        </Select.Option>
                                                                                    ))}
                                                                                </Select>
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={12}>
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'price']}
                                                                                label="Price"
                                                                                rules={[{ required: true, message: VALIDATION_MESSAGES.PRICE_REQUIRED }]}
                                                                            >
                                                                                <Input type="number" placeholder="Price" size="large" />
                                                                            </Form.Item>
                                                                        </Col>
                                                                    </Row>
                                                                </div>
                                                            ))}
                                                            <Form.Item>
                                                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="large">
                                                                    Add More
                                                                </Button>
                                                            </Form.Item>
                                                        </>
                                                    )}
                                                </Form.List>
                                            </div>
                                            )}

                                            {ticketMode === 'per_table' && (
                                            <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                                                <Form.List name="eventTables">
                                                    {(fields, { add, remove }) => (
                                                        <>
                                                            {fields.map(({ key, name, ...restField }, index) => (
                                                                <div key={key} style={{ position: 'relative', marginBottom: 24, padding: '10px 0', borderBottom: index !== fields.length - 1 ? '1px dashed #d9d9d9' : 'none' }}>
                                                                    <Form.Item {...restField} name={[name, 'id']} hidden>
                                                                        <Input type="hidden" />
                                                                    </Form.Item>
                                                                    {fields.length > 1 && (
                                                                        <Button
                                                                            type="text"
                                                                            danger
                                                                            icon={<MinusCircleOutlined />}
                                                                            onClick={() => remove(name)}
                                                                            style={{ position: 'absolute', right: 0, top: -5, zIndex: 1 }}
                                                                        />
                                                                    )}
                                                                    <Row gutter={[24, 24]}>
                                                                        <Col xs={24} md={12}>
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'label']}
                                                                                label="Table Label"
                                                                                rules={[{ required: true, message: "Table label is required" }]}
                                                                            >
                                                                                <Input placeholder="e.g. VIP Table, Couple Seat" size="large" />
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={12}>
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'personsCount']}
                                                                                label="Persons per Table"
                                                                                rules={[{ required: true, message: "Persons count is required" }]}
                                                                            >
                                                                                <Input type="number" min={1} placeholder="Count" size="large" />
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={12}>
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'currency']}
                                                                                label="Currency"
                                                                                rules={[{ required: true, message: VALIDATION_MESSAGES.CURRENCY_REQUIRED }]}
                                                                            >
                                                                                <Select placeholder="Currency" size="large" loading={currenciesLoading}>
                                                                                    {Array.isArray(currencies) && currencies.map((curr) => (
                                                                                        <Select.Option key={curr.id} value={curr.id}>
                                                                                            {curr.currency_code || curr.name || curr.code || String(curr.id)}
                                                                                        </Select.Option>
                                                                                    ))}
                                                                                </Select>
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={12}>
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'price']}
                                                                                label="Price per Table"
                                                                                rules={[{ required: true, message: VALIDATION_MESSAGES.PRICE_REQUIRED }]}
                                                                            >
                                                                                <Input type="number" placeholder="Price" size="large" />
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col xs={24} md={12}>
                                                                            <Form.Item
                                                                                {...restField}
                                                                                name={[name, 'quantity']}
                                                                                label="Number of Tables"
                                                                                rules={[{ required: true, message: "Number of tables is required" }]}
                                                                            >
                                                                                <Input type="number" min={1} placeholder="Quantity" size="large" />
                                                                            </Form.Item>
                                                                        </Col>
                                                                    </Row>
                                                                </div>
                                                            ))}
                                                            <Form.Item>
                                                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size="large">
                                                                    Add Table Option
                                                                </Button>
                                                            </Form.Item>
                                                        </>
                                                    )}
                                                </Form.List>
                                            </div>
                                            )}
                                        </Col>
                                    )}

                                    {/* Toggles Section - Matching Figma Layout */}
                                    <Col xs={24}>
                                        {/* <Form.Item label="Select Contacts List" name="contacts">
                                        <Select
                                            size="large"
                                            placeholder="Select Contacts List"
                                            options={CONTACT_LISTS}
                                        />
                                        </Form.Item> */}

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
                                            <Flex align="center" gap={12} justify="space-between">
                                                <span className="toggleLabel">Promotion</span>
                                                <Form.Item name="event_allow_promotion" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                            </Flex>

                                            <Flex align="center" gap={12} justify="space-between">
                                                <span className="toggleLabel">Registration</span>
                                                <Form.Item name="event_allow_registration" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                            </Flex>

                                            <Flex align="center" gap={12} justify="space-between">
                                                <span className="toggleLabel">RSVP</span>
                                                <Form.Item name="isRsvp" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                            </Flex>

                                            <Flex align="center" gap={12} justify="space-between">
                                                <span className="toggleLabel">Refundable</span>
                                                <Form.Item name="isRefundable" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                            </Flex>

                                            <Flex align="center" gap={12} justify="space-between">
                                                <span className="toggleLabel">Taxable</span>
                                                <Form.Item name="isTaxable" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                            </Flex>

                                            <Flex align="center" gap={12} justify="space-between">
                                                <span className="toggleLabel">Add Convenience Fee</span>
                                                <Form.Item name="addConvenienceFee" valuePropName="checked" noStyle>
                                                    <Switch />
                                                </Form.Item>
                                            </Flex>
                                        </div>
                                    </Col>
                                </Row>
                            </Form>
                        </Col>

                        <Col xs={24} md={8}>
                            <Flex vertical gap={12}>
                                <div className="mapPreview">
                                    <img src={MapImg} alt="Map preview" />
                                </div>
                                <Flex vertical gap={4}>
                                    <Text>Tags {selectedTags.length > 0 && `(${selectedTags.length} selected)`}</Text>
                                    <div className="tagsWrapper">
                                        {AVAILABLE_EVENT_TAGS.map((tag) => (
                                            <Tag
                                                key={tag}
                                                color={selectedTags.includes(tag) ? 'blue' : 'default'}
                                                className="pill-tag"
                                                style={{
                                                    cursor: 'pointer',
                                                    border: selectedTags.includes(tag) ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                                    fontWeight: selectedTags.includes(tag) ? 'bold' : 'normal',
                                                }}
                                                onClick={() => handleTagClick(tag)}
                                            >
                                                {tag}
                                            </Tag>
                                        ))}
                                    </div>
                                    {selectedTags.length > 0 && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Selected: {getTagsString()}
                                        </Text>
                                    )}
                                </Flex>
                            </Flex>
                        </Col>
                    </Row>

                    <div
                        className={`addEditStickyActions${isEmbedded ? " addEditStickyActions--embedded" : ""}`}
                    >
                        {!isEditMode && !isEmbedded && (
                            <Button
                                className="draftBtn publishBtn"
                                onClick={handleSaveDraft}
                            >
                                Save as Draft
                            </Button>
                        )}
                        <Button
                            type="primary"
                            size="large"
                            className="btnGradient publishBtn"
                            onClick={() => form.submit()}
                            loading={createEventLoading || publishEventLoading || updateEventLoading}
                        >
                            {isEditMode ? "Update" : "Create"}
                        </Button>
                    </div>

                    {/* Attendees Section */}
                    {/* <div className="attendeesWrap">
                        <Row gutter={[16, 16]} className="flex-1">
                            {users.map((user) => (
                                <Col key={user.id} xs={24} sm={12} lg={8} xl={6}>
                                    <div className="attendeeCard">
                                    <Avatar
                                        size={48}
                                        src={AvaterImg}
                                        icon={<UserOutlined />}
                                        style={{ border: "1px solid #eee" }}
                                    />
                                    <div className="attendeeInfo">
                                        <Tag className="friendTag pill-tag">{user.status}</Tag>
                                        <h4>{user.name}</h4>
                                    </div>
                                    <Button
                                        icon={<DeleteIcon />}
                                        className="attendeeDeleteBtn"
                                        type="text"
                                        size="small"
                                    />
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div> */}
                </Loader>
            </Wrapper>
        </>
    );

    if (isEmbedded) {
        return content;
    }

    return (
        <PageLayout title={isEditMode ? "Edit Event" : "Create Events"}>
            {content}
        </PageLayout>
    );
}